import { observer } from 'mobx-react-lite'
import { useCallback, useEffect } from 'react'
import { useBlocker } from 'react-router'
import { Switch, Typography } from 'shared/ui'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { uiStore } from 'shared/store/uiStore'
import { CreateNewAIAgent } from 'entities/Chatbot/actions/CreateNewAIAgent/CreateNewAIAgent'
import { useInboxLayerContext } from 'pages/settings/pages/inboxes/context/inboxLayerContext'
import styles from './styles.module.scss'

export const MessagingAiAutomation = observer(() => {
  const {
    inboxSettingsStore: { messagingAiAutomationStore },
  } = useInboxLayerContext()
  const {
    aiRepliesStore,
    showAiReplies,
    toggleShowAiReplies,
    aiRepliesSending,
    sendAiReplay,
    handleShowKeepEditingModal,
    hasUnsavedChanges,
    savedChatbotId,
  } = messagingAiAutomationStore

  const blocker = useBlocker(() => hasUnsavedChanges)
  const beforeUnloadHandler = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault()
    event.returnValue = true
  }, [])

  useEffect(() => {
    return () => {
      uiStore.setRoutesBlocker(null)
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [])

  useEffect(() => {
    uiStore.setRoutesBlocker(blocker)
    handleShowKeepEditingModal(blocker)
  }, [blocker])

  useEffect(() => {
    uiStore.setRoutesBlocker(hasUnsavedChanges ? blocker : null)

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', beforeUnloadHandler)
    } else {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [hasUnsavedChanges])

  if (!aiRepliesStore) return null

  const {
    error,
    agentItem,
    dropDownItems,
    setAiItem,
    onChangeOpen,
    onLoadMore,
    loading,
    loadingMore,
    search,
    setSearch,
  } = aiRepliesStore

  return (
    <>
      <div className={styles.inboxSection}>
        <div className={styles.description}>
          <Typography
            variant='body-lg-medium'
            color='var(--content-primary-primary)'
            ariaLabel='InboxName_title'
            tag={'div'}
            marginBottom={4}
          >
            Automate your inbox with AI agents
          </Typography>

          <Typography
            variant='body-md-regular'
            color='var(--content-primary-tertiary)'
            ariaLabel='InboxName_desc'
          >
            Let AI agents handle inbound messages with automatic responses. You can pause, resume,
            or step in anytime
          </Typography>
        </div>
        <Switch
          value={showAiReplies}
          onChange={toggleShowAiReplies}
          size='middle'
          disabled={aiRepliesSending}
        />
      </div>

      {showAiReplies && (
        <div className={styles.inboxSection}>
          <div className={styles.description}>
            <Typography
              variant='body-md-medium'
              color='var(--content-primary-primary)'
              ariaLabel='InboxName_title'
              marginBottom={4}
            >
              AI agent
            </Typography>
            <Typography
              variant='body-md-regular'
              color='var(--content-primary-tertiary)'
              ariaLabel='InboxName_desc'
            >
              Responsible for handling inbound messages here.
            </Typography>
          </div>

          <div className={styles.selectWrapper}>
            <SelectField
              placeholder={'Select an AI Agent'}
              error={error}
              item={agentItem}
              items={dropDownItems}
              search={search}
              loading={loading}
              disabled={aiRepliesSending}
              onSearch={setSearch}
              onLoadMore={onLoadMore}
              setValue={(value, item) => {
                setAiItem(value, item)
                if (savedChatbotId !== item.id) void sendAiReplay()
              }}
              dropdownProps={{
                bottomActions: (onCloseMenu) => <CreateNewAIAgent onCloseMenu={onCloseMenu} />,
                width: 230,
                maxHeight: loadingMore ? 122 : 168,
                placement: 'bottom-end',
                onChangeOpen,
                propsDropdownList: {
                  searchWrapClassName: styles.searchInput,
                  loadingMore,
                },
                PopperProps: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        offset: ({ reference }) => {
                          return [5, -reference.height - 17]
                        },
                      },
                    },
                  ],
                },
              }}
              btnProps={{
                style: {
                  height: '40px',
                  color: !agentItem ? 'var(--content-neutral-primary)' : undefined,
                  paddingRight: 8,
                },
                disabled: agentItem?.disabled,
                text: agentItem ? (
                  <Typography
                    ariaLabel='name'
                    variant='body-md-regular'
                    ellipsis
                    width={176}
                    tooltipProps={{
                      label: agentItem?.label,
                    }}
                  >
                    {agentItem?.label}
                  </Typography>
                ) : undefined,
              }}
            />
          </div>
        </div>
      )}
    </>
  )
})
